/**
 * Disability Types for MEV-Med Frontend
 * Type definitions for Disabilities (Deficiências PCD) management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de Deficiências PCD
 */

/**
 * Disability types enum
 */
export enum DisabilityType {
  FISICA = 'Física',
  VISUAL = 'Visual',
  AUDITIVA = 'Auditiva',
  INTELECTUAL = 'Intelectual',
  MULTIPLA = 'Múltipla',
  PSICOSSOCIAL = 'Psicossocial'
}

/**
 * Severity levels enum
 */
export enum DisabilitySeverity {
  LEVE = 'Leve',
  MODERADA = 'Moderada',
  GRAVE = 'Grave',
  PROFUNDA = 'Profunda'
}

/**
 * Disability classification enum
 */
export enum DisabilityClassification {
  FISICA = 'Deficiência Física',
  VISUAL = 'Deficiência Visual',
  AUDITIVA = 'Deficiência Auditiva',
  INTELECTUAL = 'Deficiência Intelectual',
  MULTIPLA = 'Deficiência Múltipla',
  TEA = 'TEA',
  PSICOSSOCIAL = 'Psicossocial'
}

/**
 * Classification origin enum
 */
export enum ClassificationOrigin {
  ACIDENTE_TRABALHO = 'Acidente de trabalho',
  CONGENITA = 'Congênita',
  ADQUIRIDA_POS_OPERATORIO = 'Adquirida em pós operatório',
  ACIDENTE_COMUM = 'Acidente comum',
  DOENCA = 'Doença',
}

/**
 * Classification type enum
 */
export enum ClassificationType {
  PERMANENTE = 'Permanente',
  TEMPORARIA = 'Temporária'
}

/**
 * Disability conclusion enum
 */
export enum DisabilityConclusion {
  APTO = 'Apto',
  INAPTO = 'Inapto',
  APTO_COM_RESTRICOES = 'Apto com Restrições',
  EM_AVALIACAO = 'Em Avaliação'
}

/**
 * Laudo attribute key enum (EAV chaves for Laudo Caracterizador)
 */
export enum LaudoAttributeKey {
  TYPE_FISICA = 'type_fisica',
  TYPE_AUDITIVA = 'type_auditiva',
  TYPE_VISUAL = 'type_visual',
  TYPE_MONOCULAR = 'type_monocular',
  TYPE_INTELECTUAL = 'type_intelectual',
  TYPE_PSICOSSOCIAL = 'type_psicossocial',
  TYPE_AUTISTA = 'type_autista',
  TYPE_MULTIPLA = 'type_multipla',
  VISUAL_CEGUEIRA = 'visual_cegueira',
  VISUAL_BAIXA_VISAO = 'visual_baixa_visao',
  VISUAL_CAMPO_VISUAL = 'visual_campo_visual',
  INTEL_COMUNICACAO = 'intel_comunicacao',
  INTEL_CUIDADO_PESSOAL = 'intel_cuidado_pessoal',
  INTEL_HABILIDADES_SOCIAIS = 'intel_habilidades_sociais',
  INTEL_RECURSOS_COMUNIDADE = 'intel_recursos_comunidade',
  INTEL_SAUDE_SEGURANCA = 'intel_saude_seguranca',
  INTEL_HABILIDADES_ACADEMICAS = 'intel_habilidades_academicas',
  INTEL_LAZER = 'intel_lazer',
  INTEL_TRABALHO = 'intel_trabalho',
  FISICA_NANISMO_ALTURA = 'fisica_nanismo_altura',
  FISICA_OUTRAS = 'fisica_outras',
  INTEL_IDADE_INICIO = 'intel_idade_inicio',
}

/**
 * Laudo detail interface (Laudo Caracterizador de Deficiência subcritérios)
 */
export interface LaudoDetail {
  tipoFisica: boolean;
  tipoAuditiva: boolean;
  tipoVisual: boolean;
  tipoMonocular: boolean;
  tipoIntelectual: boolean;
  tipoPsicossocial: boolean;
  tipoAutista: boolean;
  tipoMultipla: boolean;
  visualCegueira: boolean;
  visualBaixaVisao: boolean;
  visualCampoVisual: boolean;
  intelComunicacao: boolean;
  intelCuidadoPessoal: boolean;
  intelHabilidadesSociais: boolean;
  intelRecursosComunidade: boolean;
  intelSaudeSeguranca: boolean;
  intelHabilidadesAcademicas: boolean;
  intelLazer: boolean;
  intelTrabalho: boolean;
  fisicaNanismoAltura: string;
  fisicaOutras: string;
  intelIdadeInicio: string;
}

/**
 * Disability entity interface
 */
export interface Disability {
  id: number;
  employeeId: number | null;
  employeeName: string;
  companyId: number;
  companyName: string;
  disabilityType: string;
  disabilityDescription: string;
  severity: string;
  accommodations?: string;
  medicalCertificate?: string;
  isActive: boolean;
  registrationDate: string;
  priority: string;
  hasAccommodations: boolean;
  needsUrgentAccommodation: boolean;
  hasMedicalCertificate: boolean;
  departmentName?: string;
  occupationName?: string;
  classification?: string;
  classificationOrigin?: string;
  classificationType?: string;
  diagnosis?: string;
  doctorName?: string;
  doctorUf?: string;
  doctorCrm?: string;
  startDate?: string;
  endDate?: string;
  isRehabilitated?: boolean;
  cidCode?: string;
  cidDescription?: string;
  changesDescription?: string;
  limitationsDescription?: string;
  conclusion?: string;
  laudoDetail?: LaudoDetail;
  createdAt: string;
  updatedAt: string;
}

/**
 * Form data for creating disabilities
 */
export interface DisabilityCreateFormData {
  employeeId: number;
  employeeName?: string;
  companyId: number;
  disabilityType: string;
  disabilityDescription: string;
  severity: string;
  accommodations?: string;
  registrationDate: string;
  departmentName?: string;
  occupationName?: string;
  classification?: string;
  classificationOrigin?: string;
  classificationType?: string;
  diagnosis?: string;
  doctorName?: string;
  doctorUf?: string;
  doctorCrm?: string;
  startDate?: string;
  endDate?: string;
  isRehabilitated?: boolean;
  cidCode?: string;
  cidDescription?: string;
  changesDescription?: string;
  limitationsDescription?: string;
  conclusion?: string;
  laudoDetail?: LaudoDetail;
}

/**
 * Form data for updating disabilities
 */
export interface DisabilityUpdateFormData {
  employeeId?: number;
  employeeName?: string;
  disabilityType?: string;
  disabilityDescription?: string;
  severity?: string;
  accommodations?: string;
  registrationDate?: string;
  isActive?: boolean;
  departmentName?: string;
  occupationName?: string;
  classification?: string;
  classificationOrigin?: string;
  classificationType?: string;
  diagnosis?: string;
  doctorName?: string;
  doctorUf?: string;
  doctorCrm?: string;
  startDate?: string;
  endDate?: string;
  isRehabilitated?: boolean;
  cidCode?: string;
  cidDescription?: string;
  changesDescription?: string;
  limitationsDescription?: string;
  conclusion?: string;
  laudoDetail?: LaudoDetail;
}

/**
 * Filters for listing disabilities
 */
export interface DisabilityFilters {
  search?: string;
  companyId?: number;
  employeeId?: number;
  employeeName?: string;
  disabilityType?: string;
  severity?: string;
  isActive?: boolean;
  hasAccommodations?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Response for list endpoint with pagination
 */
export interface DisabilityListResponse {
  disabilities: Disability[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Medical certificate info
 */
export interface MedicalCertificateInfo {
  id: number;
  fileName: string;
  fileExtension: string;
  filePath: string;
}

/**
 * Disability statistics
 */
export interface DisabilityStatistics {
  totalDisabilities: number;
  totalEmployees: number;
  byType: Record<string, number>;
  bySeverity: Record<string, number>;
  withAccommodations: number;
  withoutAccommodations: number;
  quotaCompliance: number;
  accommodationRate: number;
}

/**
 * List of disability types for select options
 */
export const DISABILITY_TYPE_OPTIONS = Object.values(DisabilityType);

/**
 * List of severity levels for select options
 */
export const SEVERITY_LEVEL_OPTIONS = Object.values(DisabilitySeverity);

/**
 * List of disability classifications for select options
 */
export const DISABILITY_CLASSIFICATION_OPTIONS = Object.values(DisabilityClassification);

/**
 * List of classification origins for select options
 */
export const CLASSIFICATION_ORIGIN_OPTIONS = Object.values(ClassificationOrigin);

/**
 * List of classification types for select options
 */
export const CLASSIFICATION_TYPE_OPTIONS = Object.values(ClassificationType);

/**
 * List of conclusions for select options
 */
export const DISABILITY_CONCLUSION_OPTIONS = Object.values(DisabilityConclusion);

/**
 * Props for UI components
 */
export interface DisabilityTableProps {
  disabilities: Disability[];
  onEdit: (disability: Disability) => void;
  onDelete: (id: number) => void;
  onViewCertificate?: (disability: Disability) => void;
  onDeactivate?: (id: number) => void;
  onReactivate?: (id: number) => void;
  loading?: boolean;
}

export interface DisabilityFormProps {
  initialData?: Disability;
  onSubmit: (data: DisabilityCreateFormData | DisabilityUpdateFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
}

export interface DisabilityFiltersProps {
  filters: DisabilityFilters;
  onChange: (filters: DisabilityFilters) => void;
  onSearch: () => void;
}

/**
 * Helper function to get priority color
 */
export function getPriorityColor(priority: string): 'error' | 'warning' | 'success' | 'default' {
  switch (priority) {
    case 'Alta':
      return 'error';
    case 'Média':
      return 'warning';
    case 'Baixa':
      return 'success';
    default:
      return 'default';
  }
}

/**
 * Helper function to get severity color
 */
export function getSeverityColor(severity: string): 'error' | 'warning' | 'info' | 'success' {
  switch (severity) {
    case DisabilitySeverity.PROFUNDA:
      return 'error';
    case DisabilitySeverity.GRAVE:
      return 'error';
    case DisabilitySeverity.MODERADA:
      return 'warning';
    case DisabilitySeverity.LEVE:
      return 'info';
    default:
      return 'info';
  }
}

/**
 * Helper function to format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
}
